/**
 * WindPressureCentersController — auto-renders H/L pressure-center labels
 * on the map whenever the Wind layer is visible.
 *
 * Spec: data/wind-validity-check-spec-2026-05-28.md
 *
 * • Reads qa.lowCenters + qa.highCenters from /api/wind-manifest
 * • Builds a GeoJSON FeatureCollection and pushes it into the
 *   'wind-pressure-centers' source (see src/map/layers/windPressureCenters.ts)
 * • Visibility mirrors layers.wind — on when wind is on, off otherwise
 * • Refreshes when the wind cycle changes (watches windForecastHour as
 *   a proxy for "user interacted with wind" — manifest poll is cheap)
 * • Effect-only component — returns null
 */
import { useEffect, useRef } from 'react';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useWindPrefsStore } from '../../stores/windPrefsStore';
import { getMapInstance } from '../MapView';
import {
  addWindPressureCentersLayers,
  attachPressureCentersClickHandler,
  buildPressureCentersFC,
  setPressureCentersVisibility,
  updatePressureCentersData,
  type PressureCentersData,
} from '../layers/windPressureCenters';

interface WindManifestQa {
  lowCenters?:  Array<{ lat: number; lon: number; pressureHpa: number; score: number; level: number }>;
  highCenters?: Array<{ lat: number; lon: number; pressureHpa: number; score: number; level: number }>;
  passed?: boolean;
}
interface WindManifest {
  cycle: string;
  generatedAt: string;
  qa?: WindManifestQa;
}

async function fetchManifest(signal: AbortSignal): Promise<WindManifest | null> {
  try {
    const r = await fetch('/api/wind-manifest', { signal });
    if (!r.ok) return null;
    return (await r.json()) as WindManifest;
  } catch {
    return null;
  }
}

export function WindPressureCentersController() {
  const visible = useLayerVisibilityStore((s) => s.layers.wind);
  // Watch forecastHour so a new cycle fetch is triggered after the user
  // scrubs the slider (same cycle, different hour → same manifest, cheap).
  const forecastHour = useWindPrefsStore((s) => s.windForecastHour);

  const installedRef  = useRef(false);
  const cleanupRef    = useRef<(() => void) | null>(null);
  const abortRef      = useRef<AbortController | null>(null);
  // Last fetched cycle key — avoid redundant re-fetches when only forecastHour changes.
  const lastCycleRef  = useRef<string | null>(null);

  // Mount source + layers once, idempotently.
  const ensureLayers = () => {
    const map = getMapInstance();
    if (!map || installedRef.current) return;
    addWindPressureCentersLayers(map);
    cleanupRef.current = attachPressureCentersClickHandler(map);
    installedRef.current = true;

    // Re-mount on setStyle() rebuilds.
    const onStyleData = () => {
      const m = getMapInstance();
      if (!m) return;
      addWindPressureCentersLayers(m);
      // Re-wire click handlers on fresh layers.
      if (cleanupRef.current) cleanupRef.current();
      cleanupRef.current = attachPressureCentersClickHandler(m);
    };
    map.on('styledata', onStyleData);
    // Store a second cleanup for the styledata listener.
    const prevCleanup = cleanupRef.current;
    cleanupRef.current = () => {
      prevCleanup?.();
      map.off('styledata', onStyleData);
    };
  };

  // Fetch manifest + update source data.
  const fetchAndUpdate = (force = false) => {
    const map = getMapInstance();
    if (!map) return;

    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      const manifest = await fetchManifest(ac.signal);
      if (!manifest || ac.signal.aborted) return;

      // Skip re-fetch if the cycle hasn't changed and not forced.
      if (!force && manifest.cycle === lastCycleRef.current) return;
      lastCycleRef.current = manifest.cycle;

      const qa = manifest.qa;
      const data: PressureCentersData = {
        lowCenters:  qa?.lowCenters  ?? [],
        highCenters: qa?.highCenters ?? [],
        cycle:       manifest.cycle,
        generatedAt: manifest.generatedAt,
        passed:      qa?.passed ?? null,
      };
      const fc = buildPressureCentersFC(data);
      updatePressureCentersData(map, fc);
    })();
  };

  // Main visibility effect.
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;

    if (visible) {
      ensureLayers();
      setPressureCentersVisibility(map, true);
      fetchAndUpdate();
    } else {
      setPressureCentersVisibility(map, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Re-fetch when forecastHour changes and wind is visible (cycle may have changed).
  useEffect(() => {
    if (!visible) return;
    fetchAndUpdate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastHour]);

  // Final cleanup.
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      abortRef.current?.abort();
    };
  }, []);

  return null;
}
